import { useAuthStore } from "../stores/authStore";

// Base API configuration
// Fail explicitly if env var is missing in production
const getApiBaseUrl = () => {
  const url = process.env.REACT_APP_API_BASE_URL;
  if (!url && process.env.NODE_ENV === 'production') {
    throw new Error('REACT_APP_API_BASE_URL must be set in production');
  }
  return url || 'http://localhost:8080/api';
};

export const API_BASE_URL = getApiBaseUrl();

// Create axios-like fetch wrapper with automatic token refresh
class ApiClient {
  constructor(baseURL) {
    this.baseURL = baseURL;
    this.isRefreshing = false;
    this.failedQueue = [];
  }

  // Process failed requests after token refresh
  processQueue(error, token = null) {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else {
        resolve(token);
      }
    });

    this.failedQueue = [];
  }

  // Refresh access token using httpOnly cookie
  async refreshAccessToken() {
    const { updateTokens, logout } = useAuthStore.getState();

    try {
      const response = await fetch(`${this.baseURL}/auth/token/refresh`, {
        method: "POST",
        credentials: 'include', // Send httpOnly cookie automatically
      });

      if (!response.ok) {
        throw new Error("Token refresh failed");
      }

      const data = await response.json();
      updateTokens(data.accessToken);
      return data.accessToken;
    } catch (error) {
      logout();
      window.location.href = "/sign-in";
      throw error;
    }
  }

  async request(endpoint, options = {}) {
    // Handle query parameters
    let url = `${this.baseURL}${endpoint}`;
    if (options.params) {
      const searchParams = new URLSearchParams();
      Object.entries(options.params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, value);
        }
      });
      const queryString = searchParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }

    const { getAccessToken } = useAuthStore.getState();
    const token = getAccessToken();

    // Remove params and headers from options before spreading
    const { params, headers: optionHeaders, ...fetchOptions } = options;

    const isFormData = fetchOptions.body instanceof FormData;

    const config = {
      credentials: 'include', // Include httpOnly cookies in all requests
      ...fetchOptions,
      headers: {
        // Don't set Content-Type for FormData — browser sets it with boundary
        ...(!isFormData && { "Content-Type": "application/json" }),
        ...(token && { Authorization: `Bearer ${token}` }),
        ...optionHeaders,
      },
    };

    if (config.body && typeof config.body === "object" && !isFormData) {
      config.body = JSON.stringify(config.body);
    }

    try {
      const response = await fetch(url, config);

      // Handle non-JSON responses
      const contentType = response.headers.get("content-type");
      let data;

      if (contentType && contentType.includes("application/json")) {
        data = await response.json();
      } else {
        data = await response.text();
      }

      if (!response.ok) {
        // Endpoints where 401 means bad credentials or would cause infinite loops
        const skipRefreshEndpoints = [
          "/auth/login",
          "/auth/token/refresh",
          "/auth/register",
          "/auth/password-reset",
          "/auth/forgot-password",
          "/auth/verify-email",
          "/auth/resend-verification",
        ];
        const shouldSkipRefresh = skipRefreshEndpoints.some((path) =>
          endpoint.includes(path)
        );

        // Handle authentication errors with token refresh
        if (response.status === 401 && token && !shouldSkipRefresh) {
          // Don't refresh for unauthenticated auth endpoints to avoid infinite loops
          if (this.isRefreshing) {
            // If already refreshing, queue this request
            return new Promise((resolve, reject) => {
              this.failedQueue.push({ resolve, reject });
            }).then(() => {
              // Retry the original request with new token
              return this.request(endpoint, options);
            });
          }

          this.isRefreshing = true;

          try {
            const newToken = await this.refreshAccessToken();
            this.processQueue(null, newToken);
            this.isRefreshing = false;

            // Retry the original request with new token
            return this.request(endpoint, options);
          } catch (refreshError) {
            this.processQueue(refreshError, null);
            this.isRefreshing = false;
            throw refreshError;
          }
        } else if (response.status === 401) {
          // For skipped auth endpoints (login, register, etc.), let the auth form handle the error
          if (shouldSkipRefresh) {
            // Just throw the error - auth components will display the error message
          } else {
            // For non-auth endpoints without a token, logout and redirect to sign-in
            useAuthStore.getState().logout();
            window.location.href = "/sign-in";
          }
        }

        throw new Error(
          data.message || `HTTP error! status: ${response.status}`
        );
      }

      return data;
    } catch (error) {
      console.error("API request failed:", error);

      // Ensure error has proper structure to prevent UI freezing
      if (error instanceof Error) {
        throw error;
      } else {
        // Create a proper Error object if it's not already one
        throw new Error(error?.message || "Unknown API error");
      }
    }
  }

  get(endpoint, options = {}) {
    return this.request(endpoint, { method: "GET", ...options });
  }

  post(endpoint, data, options = {}) {
    return this.request(endpoint, {
      method: "POST",
      body: data,
      ...options,
    });
  }

  put(endpoint, data, options = {}) {
    return this.request(endpoint, {
      method: "PUT",
      body: data,
      ...options,
    });
  }

  patch(endpoint, data, options = {}) {
    return this.request(endpoint, {
      method: "PATCH",
      body: data,
      ...options,
    });
  }

  delete(endpoint, options = {}) {
    return this.request(endpoint, { method: "DELETE", ...options });
  }

  // Get blob data (for file downloads like PDF, Excel)
  // Build a rich Error from a non-OK blob response. Blob endpoints still
  // return the standard JSON error envelope (`{message, error, ...}`) on
  // failure — we parse it so callers can surface the backend's friendly
  // reason in toasts. Resulting error exposes:
  //   err.message               — backend message or fallback "HTTP error! status: N"
  //   err.status                — numeric HTTP status
  //   err.response.data.message — backend message (axios-shape for the
  //                               `e?.response?.data?.message` access pattern
  //                               used by handleExport sites)
  // Falls back to a generic message if the body isn't parseable.
  async _buildBlobError(response) {
    let backendMessage = null;
    try {
      const text = await response.text();
      if (text) {
        try {
          const parsed = JSON.parse(text);
          backendMessage = parsed?.message || parsed?.error || null;
        } catch {
          // Non-JSON body — only use it if it's short and likely human-readable
          backendMessage = text.length < 200 ? text : null;
        }
      }
    } catch {
      // Body read failure — fall through to generic message
    }
    const message = backendMessage || `HTTP error! status: ${response.status}`;
    const err = new Error(message);
    err.status = response.status;
    err.response = {
      status: response.status,
      data: backendMessage ? { message: backendMessage } : null,
    };
    return err;
  }

  async getBlob(endpoint, options = {}) {
    let url = `${this.baseURL}${endpoint}`;

    const { getAccessToken } = useAuthStore.getState();
    const token = getAccessToken();

    const { headers: optionHeaders, ...restOptions } = options;

    const config = {
      credentials: 'include',
      ...restOptions,
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
        ...optionHeaders,
      },
    };

    try {
      const response = await fetch(url, config);

      // Handle 401 and retry with new token
      if (response.status === 401 && !config._retry) {
        if (this.isRefreshing) {
          // Wait for the ongoing refresh
          return new Promise((resolve, reject) => {
            this.failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              config.headers.Authorization = `Bearer ${token}`;
              config._retry = true;
              return fetch(url, config).then((res) => res.blob());
            })
            .catch((err) => Promise.reject(err));
        }

        this.isRefreshing = true;
        config._retry = true;

        try {
          const newToken = await this.refreshAccessToken();
          this.processQueue(null, newToken);
          config.headers.Authorization = `Bearer ${newToken}`;
          const retryResponse = await fetch(url, config);
          this.isRefreshing = false;

          if (!retryResponse.ok) {
            throw await this._buildBlobError(retryResponse);
          }

          return await retryResponse.blob();
        } catch (refreshError) {
          this.processQueue(refreshError, null);
          this.isRefreshing = false;
          throw refreshError;
        }
      }

      if (!response.ok) {
        throw await this._buildBlobError(response);
      }

      return await response.blob();
    } catch (error) {
      console.error("API Error:", error);
      throw error;
    }
  }

  // Get blob + server-suggested filename (parsed from Content-Disposition).
  // Used by file downloads where the server's filename matters
  // (Excel reports, etc.). Blob-only callers should use getBlob instead.
  // Returns { blob, filename } — filename is null when the header is absent.
  async getBlobWithFilename(endpoint, options = {}) {
    let url = `${this.baseURL}${endpoint}`;

    const { getAccessToken } = useAuthStore.getState();
    const token = getAccessToken();

    const { headers: optionHeaders, ...restOptions } = options;

    const config = {
      credentials: 'include',
      ...restOptions,
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
        ...optionHeaders,
      },
    };

    const parseFilename = (response) => {
      const disposition = response.headers.get("Content-Disposition");
      if (!disposition) return null;
      const match = disposition.match(/filename=(.+?)(?:;|$)/);
      return match ? match[1].replace(/"/g, "") : null;
    };

    try {
      let response = await fetch(url, config);

      if (response.status === 401 && !config._retry) {
        if (this.isRefreshing) {
          return new Promise((resolve, reject) => {
            this.failedQueue.push({ resolve, reject });
          })
            .then(async (newToken) => {
              config.headers.Authorization = `Bearer ${newToken}`;
              config._retry = true;
              const retried = await fetch(url, config);
              if (!retried.ok) throw new Error(`HTTP error! status: ${retried.status}`);
              return { blob: await retried.blob(), filename: parseFilename(retried) };
            });
        }

        this.isRefreshing = true;
        config._retry = true;

        try {
          const newToken = await this.refreshAccessToken();
          this.processQueue(null, newToken);
          config.headers.Authorization = `Bearer ${newToken}`;
          response = await fetch(url, config);
          this.isRefreshing = false;

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          return { blob: await response.blob(), filename: parseFilename(response) };
        } catch (refreshError) {
          this.processQueue(refreshError, null);
          this.isRefreshing = false;
          throw refreshError;
        }
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return { blob: await response.blob(), filename: parseFilename(response) };
    } catch (error) {
      console.error("API Error:", error);
      throw error;
    }
  }

  // Post blob data (for POST requests that return files like PDF, Excel)
  async postBlob(endpoint, data, options = {}) {
    let url = `${this.baseURL}${endpoint}`;

    const { getAccessToken } = useAuthStore.getState();
    const token = getAccessToken();

    const { headers: optionHeaders, ...restOptions } = options;

    const config = {
      method: 'POST',
      credentials: 'include',
      ...restOptions,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...optionHeaders,
      },
      body: JSON.stringify(data),
    };

    try {
      const response = await fetch(url, config);

      // Handle 401 and retry with new token
      if (response.status === 401 && !config._retry) {
        if (this.isRefreshing) {
          // Wait for the ongoing refresh
          return new Promise((resolve, reject) => {
            this.failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              config.headers.Authorization = `Bearer ${token}`;
              config._retry = true;
              return fetch(url, config).then((res) => res.blob());
            })
            .catch((err) => Promise.reject(err));
        }

        this.isRefreshing = true;
        config._retry = true;

        try {
          const newToken = await this.refreshAccessToken();
          this.processQueue(null, newToken);
          config.headers.Authorization = `Bearer ${newToken}`;
          const retryResponse = await fetch(url, config);
          this.isRefreshing = false;

          if (!retryResponse.ok) {
            throw new Error(`HTTP error! status: ${retryResponse.status}`);
          }

          return await retryResponse.blob();
        } catch (refreshError) {
          this.processQueue(refreshError, null);
          this.isRefreshing = false;
          throw refreshError;
        }
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.blob();
    } catch (error) {
      console.error("API Error:", error);
      throw error;
    }
  }
}

export const apiClient = new ApiClient(API_BASE_URL);

// Common API error handler
export const handleApiError = (error) => {
  if (error.response) {
    // Server responded with error status
    return error.response.data?.message || "Server error occurred";
  } else if (error.request) {
    // Request was made but no response received
    return "Network error - please check your connection";
  } else {
    // Something else happened
    return error.message || "An unexpected error occurred";
  }
};
